import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark';

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  school_type: string;
  pricing_tier: string;
  logo_url?: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  permissions: string[];
  phone: string;
  email?: string | null;
}

interface AuthStore {
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  theme: Theme;
  setAuth: (user: User, tenant: Tenant, token: string) => void;
  setAccessToken: (token: string) => void;
  clearAuth: () => void;
  hasPermission: (perm: string) => boolean;
  hasRole: (role: string) => boolean;
  setTheme: (theme: Theme) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      accessToken: null,
      isAuthenticated: false,
      theme: 'light',

      setAuth: (user, tenant, accessToken) =>
        set({ user, tenant, accessToken, isAuthenticated: true }),

      setAccessToken: (accessToken) => set({ accessToken }),

      clearAuth: () =>
        set({ user: null, tenant: null, accessToken: null, isAuthenticated: false }),

      hasPermission: (perm) =>
        get().user?.permissions.includes(perm) ?? false,

      hasRole: (role) =>
        get().user?.role === role,

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: 'usms-auth',
      partialize: (s) => ({ user: s.user, tenant: s.tenant, isAuthenticated: s.isAuthenticated, theme: s.theme }),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme ?? 'light');
      },
    }
  )
);
